import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import { observer } from 'mobx-react-lite';
import { type MouseEvent } from 'react';
import { s3Parser } from 's3u';
import { DropDownWrap, IconButton } from '../../../../../../components';
import { useStore } from '../../../../../../store';
import {
  getStorageProvidersInfo,
  S3_SERVICES,
  S3ServiceName,
} from '../../../../../account/store/services/types';
import urlToNavigationItems from './urlToNavigationItems';

export default function Navigation({
  onNavigate,
  url,
}: {
  onNavigate?: (url: string) => void;
  url: string;
}) {
  const { accountStore } = useStore();
  const selectedProvider =
    accountStore.account?.configurations.storages.selectedProvider;

  const [root, ...items] = urlToNavigationItems(
    url || (selectedProvider ? `https://${selectedProvider}` : '') || '',
  );

  function onNavigationClick(e: MouseEvent<HTMLAnchorElement>) {
    e.preventDefault();
    onNavigate?.(e.currentTarget.href);
  }

  return (
    <Breadcrumbs>
      <div>
        {root ? (
          <Link href={root.url} onClick={onNavigationClick}>
            {root.name}
          </Link>
        ) : (
          'Provider'
        )}
        <ProviderSelect onNavigate={onNavigate} url={url} />
      </div>

      {items.map((item) => (
        <Link href={item.url} key={item.url} onClick={onNavigationClick}>
          {item.name}
        </Link>
      ))}
    </Breadcrumbs>
  );
}

const ProviderSelect = observer(function ProviderSelect({
  onNavigate,
  url,
}: {
  onNavigate?: (url: string) => void;
  url: string;
}) {
  const { accountStore } = useStore();

  function onSelect(provider: string) {
    if (S3_SERVICES.includes(provider as S3ServiceName)) {
      const s3Provider = s3Parser.getProviderById(provider);
      if (s3Provider) {
        onNavigate?.(s3Provider.getEndpoint());
        return;
      }
    }
    onNavigate?.(`https://${provider}`);
  }

  const providerInfo = getStorageProvidersInfo();

  const storages = accountStore.account?.configurations.storages.items || [];
  const options = storages.map((storage) => {
    return {
      label:
        providerInfo.find((p) => p.value === storage.provider)?.label ||
        storage.provider,
      value: storage.provider,
    };
  });

  const selected = storages.find((storage) =>
    url.includes(storage.provider),
  )?.provider;

  if (options.length < 2) {
    return null;
  }

  return (
    <DropDownWrap onSelect={onSelect} options={options} selected={selected}>
      <IconButton icon="angle-down" size="small" />
    </DropDownWrap>
  );
});
